import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './SignupPage.css';

// Logo
import logoSvg from '../../assets/landing/icons/logo.svg';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    companyStreet: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.company || formData.company.trim() === '') {
      setError('Company name is required');
      setLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    if (!agreeTerms) {
      setError('Please agree to the terms and conditions');
      setLoading(false);
      return;
    }

    try {
      await signup(formData);
      // Redirect to email verification page
      navigate('/verify-email', { state: { email: formData.email } });
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page signup-page">
      {/* Top accent bar */}
      <div className="top-accent-bar"></div>

      <div className="auth-container">
        {/* Left side - Branding */}
        <div className="auth-branding">
          <Link to="/" className="auth-logo">
            <img src={logoSvg} alt="Marquee Couriers" />
          </Link>
          <div className="auth-branding-content">
            <h1>Get Started Today</h1>
            <p>
              Join thousands of businesses using Marquee Couriers for smarter,
              faster, and verified deliveries.
            </p>
            <div className="auth-features">
              <div className="auth-feature">
                <CheckIcon />
                <span>Free 14-day trial</span>
              </div>
              <div className="auth-feature">
                <CheckIcon />
                <span>No credit card required</span>
              </div>
              <div className="auth-feature">
                <CheckIcon />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="auth-form-section">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h2>Create Account</h2>
              <p>Fill in your details to get started</p>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              {error && (
                <div className="error-message" style={{
                  padding: '12px',
                  marginBottom: '20px',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <div className="input-wrapper">
                    <UserIcon />
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company Name</label>
                  <div className="input-wrapper">
                    <BuildingIcon />
                    <input
                      type="text"
                      id="company"
                      name="company"
                      placeholder="Your company name"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="companyStreet">Company Address</label>
                <div className="input-wrapper">
                  <LocationIcon />
                  <input
                    type="text"
                    id="companyStreet"
                    name="companyStreet"
                    placeholder="Street address"
                    value={formData.companyStreet}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="companyCity">City</label>
                  <div className="input-wrapper">
                    <CityIcon />
                    <input
                      type="text"
                      id="companyCity"
                      name="companyCity"
                      placeholder="City"
                      value={formData.companyCity}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="companyState">State</label>
                  <div className="input-wrapper">
                    <StateIcon />
                    <input
                      type="text"
                      id="companyState"
                      name="companyState"
                      placeholder="State"
                      value={formData.companyState}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="companyZip">ZIP Code</label>
                  <div className="input-wrapper">
                    <ZipIcon />
                    <input
                      type="text"
                      id="companyZip"
                      name="companyZip"
                      placeholder="ZIP"
                      value={formData.companyZip}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <EmailIcon />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-wrapper">
                    <LockIcon />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="Create password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={8}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="input-wrapper">
                    <LockIcon />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                    >
                      {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="password-requirements">
                <span className={formData.password.length >= 8 ? 'met' : ''}>
                  <CheckCircleIcon /> At least 8 characters
                </span>
                <span className={/[A-Z]/.test(formData.password) ? 'met' : ''}>
                  <CheckCircleIcon /> One uppercase letter
                </span>
                <span className={/[0-9]/.test(formData.password) ? 'met' : ''}>
                  <CheckCircleIcon /> One number
                </span>
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="checkmark"></span>
                  <span>I agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a></span>
                </label>
              </div>

              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <div className="auth-divider">
                <span>or sign up with</span>
              </div>

              <div className="social-login">
                <button type="button" className="social-btn">
                  <GoogleIcon />
                  <span>Google</span>
                </button>
              </div>
            </form>

            <p className="auth-switch">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Icon Components
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6667 5L7.50001 14.1667L3.33334 10" stroke="#C9A32C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M4.5 7L6 8.5L9.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="6" r="3.5" stroke="#9C9AA3" strokeWidth="1.5"/>
    <path d="M3.5 17.5C3.5 14.4624 6.46243 12 10 12C13.5376 12 16.5 14.4624 16.5 17.5" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const BuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="2.5" width="14" height="15" rx="1" stroke="#9C9AA3" strokeWidth="1.5"/>
    <path d="M7 6H9M7 9H9M7 12H9M11 6H13M11 9H13M11 12H13" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 17.5V15C8 14.4477 8.44772 14 9 14H11C11.5523 14 12 14.4477 12 15V17.5" stroke="#9C9AA3" strokeWidth="1.5"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.33334 3.33334H16.6667C17.5833 3.33334 18.3333 4.08334 18.3333 5.00001V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33334C2.41667 16.6667 1.66667 15.9167 1.66667 15V5.00001C1.66667 4.08334 2.41667 3.33334 3.33334 3.33334Z" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.3333 5L10 10.8333L1.66667 5" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.33334" y="9.16666" width="13.3333" height="9.16667" rx="2" stroke="#9C9AA3" strokeWidth="1.5"/>
    <path d="M5.83334 9.16666V5.83333C5.83334 3.53214 7.69882 1.66666 10 1.66666C12.3012 1.66666 14.1667 3.53214 14.1667 5.83333V9.16666" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.66667 10C1.66667 10 4.16667 4.16666 10 4.16666C15.8333 4.16666 18.3333 10 18.3333 10C18.3333 10 15.8333 15.8333 10 15.8333C4.16667 15.8333 1.66667 10 1.66667 10Z" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="10" cy="10" r="2.5" stroke="#9C9AA3" strokeWidth="1.5"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.82084 8.82084C8.50587 9.13581 8.33334 9.55978 8.33334 10C8.33334 10.4602 8.50894 10.9019 8.82084 11.2138C9.13275 11.5257 9.57448 11.7013 10.0347 11.7013C10.4949 11.7013 10.9366 11.5257 11.2486 11.2138" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C4.16667 16.6667 1.66667 10 1.66667 10C2.69334 8.06825 4.10426 6.38051 5.8 5.05L14.95 14.95Z" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8.25 3.53333C8.82361 3.39907 9.41094 3.33195 10 3.33333C15.8333 3.33333 18.3333 10 18.3333 10C17.8398 10.9463 17.2467 11.8373 16.5667 12.6583" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M1.66667 1.66666L18.3333 18.3333" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.1713 8.36788H17.5V8.33329H10V11.6666H14.7096C14.0225 13.607 12.1763 15 10 15C7.23875 15 5 12.7612 5 9.99996C5 7.23871 7.23875 4.99996 10 4.99996C11.2746 4.99996 12.4342 5.48079 13.3171 6.26621L15.6742 3.90913C14.1858 2.52204 12.195 1.66663 10 1.66663C5.39792 1.66663 1.66667 5.39788 1.66667 9.99996C1.66667 14.602 5.39792 18.3333 10 18.3333C14.6021 18.3333 18.3333 14.602 18.3333 9.99996C18.3333 9.44121 18.2758 8.89579 18.1713 8.36788Z" fill="#FFC107"/>
    <path d="M2.6275 6.12121L5.36542 8.12913C6.10625 6.29496 7.90042 4.99996 10 4.99996C11.2746 4.99996 12.4342 5.48079 13.3171 6.26621L15.6742 3.90913C14.1858 2.52204 12.195 1.66663 10 1.66663C6.79917 1.66663 4.02334 3.47371 2.6275 6.12121Z" fill="#FF3D00"/>
    <path d="M10 18.3334C12.1525 18.3334 14.1083 17.5096 15.5871 16.17L13.0079 13.9875C12.1431 14.6452 11.0864 15.0009 10 15C7.8325 15 5.99209 13.618 5.29875 11.6892L2.58125 13.783C3.96042 16.4817 6.76125 18.3334 10 18.3334Z" fill="#4CAF50"/>
    <path d="M18.1713 8.36796H17.5V8.33337H10V11.6667H14.7096C14.3809 12.5902 13.7889 13.3972 13.0067 13.988L13.0079 13.9871L15.5871 16.1696C15.4046 16.3355 18.3333 14.1667 18.3333 10C18.3333 9.44129 18.2758 8.89587 18.1713 8.36796Z" fill="#1976D2"/>
  </svg>
);

const LocationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 11.6667C11.8409 11.6667 13.3333 10.1743 13.3333 8.33333C13.3333 6.49238 11.8409 5 10 5C8.15905 5 6.66667 6.49238 6.66667 8.33333C6.66667 10.1743 8.15905 11.6667 10 11.6667Z" stroke="#9C9AA3" strokeWidth="1.5"/>
    <path d="M10 1.66666C7.69882 1.66666 5.83334 3.53214 5.83334 5.83333C5.83334 9.16666 10 15 10 15C10 15 14.1667 9.16666 14.1667 5.83333C14.1667 3.53214 12.3012 1.66666 10 1.66666V1.66666Z" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 17.5H17M4.5 17.5V6.5L10 3L15.5 6.5V17.5" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.5 10H8.5M7.5 13H8.5M11.5 10H12.5M11.5 13H12.5" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const StateIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 7.5L10 3.33333L17.5 7.5M2.5 7.5V15L10 18.3333M2.5 7.5L10 11.6667M17.5 7.5V15L10 18.3333M17.5 7.5L10 11.6667M10 11.6667V18.3333" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ZipIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.66667 2.5H13.3333C14.2538 2.5 15 3.24619 15 4.16667V15.8333C15 16.7538 14.2538 17.5 13.3333 17.5H6.66667C5.74619 17.5 5 16.7538 5 15.8333V4.16667C5 3.24619 5.74619 2.5 6.66667 2.5Z" stroke="#9C9AA3" strokeWidth="1.5"/>
    <path d="M7.5 6.66666H12.5M7.5 10H12.5M7.5 13.3333H10" stroke="#9C9AA3" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export default SignupPage;
